/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable @typescript-eslint/no-shadow */
import FEATURES_APP from '@configs/featureflags';
import { DecimalDigits } from '@constants/enums';
import { Avatar, Button } from '@efexteam/components';
import { Actions, Can, Subjects, useRole } from '@efexteam/permission-manager';
import { Box, Typography } from '@mui/material';
import ConvertModal from '@pages/dashboard/components/wallets/ConvertModal';
import { useMemo } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { missingPayablesImage } from '../../assets';
import DataLoadingWrapper from '../../components/common/DataLoadingWrapper';
import { Language } from '../../configs/language';
import { StateSpan } from '../../constants/states';
import useBoolean from '../../hooks/useBoolean';
import useCombineBalances from '../../hooks/useCombinedBalances';
import useImmediateApiCall from '../../hooks/useImmediateApiCall';
import clientSvc from '../../services/client/clientSvc';
import {
  capitalizeFirst,
  convertDateToHumanReadable,
  formatCurrency,
} from '../../utils/formater';
import MissingInfo from '../missing/MissingInfo';
import { selectProfileData } from '../profile/store/selectors';

interface IBalanceHistory {
  uuid: string;
  status: string;
  depositAmount: string;
  type: string;
  origin: string;
  updated_at: string;
  amount: string;
  currency: string;
  user: string;
}

const RenderContent = ({
  data,
  showConvertModal,
}: {
  data: IBalanceHistory[];
  showConvertModal: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language.toUpperCase() as Language;
  const profile = useSelector(selectProfileData);
  const { isPaymentAnalyst } = useRole();

  if (!language) return null;

  if (data === null || !data.length)
    return (
      <Box className="missing">
        <Can action={Actions.CREATE} subject={Subjects.CONVERSIONS}>
          <Box className="ml-auto flex justify-end p-5 gap-3">
            {FEATURES_APP.convertion && (
              <Button
                appearance="primary"
                size="large"
                onClick={showConvertModal}
                className="convert-step"
                disabled={!profile?.canUserWithdraw}
              >
                {t(isPaymentAnalyst ? 'walletConfigureConvert' : 'convert')}
              </Button>
            )}
          </Box>
        </Can>
        <MissingInfo
          boxTitle={t('balances')}
          image={missingPayablesImage}
          messageTitle={t('missingDataBalancesTitle')}
          messageText={t('missingDataBalancesText')}
        />
      </Box>
    );

  return (
    <Box>
      <Box className="flex justify-between items-center gap-2 py-5">
        <Typography variant="h6">{t('balanceMessage')}</Typography>
        <Can action={Actions.CREATE} subject={Subjects.CONVERSIONS}>
          <Box className="ml-auto flex gap-3">
            {FEATURES_APP.convertion && (
              <Button
                appearance="primary"
                size="large"
                onClick={showConvertModal}
                className="convert-step"
                disabled={!profile?.canUserWithdraw}
              >
                {t('convert')}
              </Button>
            )}
          </Box>
        </Can>
      </Box>
      <table>
        <thead>
          <tr>
            <th className="py-3">{t('RepresentativeName')}</th>
            <th className="py-3">{t('paymentStatus')}</th>
            <th className="py-3">{t('depositAmount')}</th>
            <th className="py-3">{t('typeTransaction')}</th>
            <th className="py-3">{t('dateTransaction')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((element: IBalanceHistory, index: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={`item-balance-${index}`}>
              <td>
                <section className="flex space-x-2 items-center">
                  <Avatar username={element.user} size="sm" />
                  <p className="m-0 ml-2 text-sm inline">{element.user}</p>
                </section>
              </td>
              <td>
                <StateSpan status={element.status} />
              </td>
              <td
                className={
                  element.type === 'Add' || element.origin === 'Wallet_payment'
                    ? 'text-green-700'
                    : 'text-red-700'
                }
              >
                {element.type === 'Remove' && '-'}
                {formatCurrency(
                  element.amount,
                  DecimalDigits.DEFAULT_DIGITS,
                  DecimalDigits.DEFAULT_MIN_DIGITS,
                  element.currency
                )}
                {element.currency}
              </td>
              <td>
                <StateSpan
                  status={
                    element.origin === 'Bitso' && element.type === 'Remove'
                      ? 'Withdrawal'
                      : element.origin
                  }
                />
              </td>
              <td>
                {convertDateToHumanReadable(
                  new Date(element.updated_at).toISOString(),
                  language.toLowerCase()
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

const Balances = () => {
  const navigate = useNavigate();
  const profile = useSelector(selectProfileData);
  const { data: balances, loading: loadingData } = useImmediateApiCall(() =>
    clientSvc.getClientBalances()
  );
  const [
    isConvertModalOpen,
    { setTrue: showConvertModal, setFalse: hideConvertModal },
  ] = useBoolean(false);
  const { dataMixed, reloadWallet } = useCombineBalances();

  const data = useMemo(() => {
    if (balances) {
      return balances.map((obj) => ({
        uuid: obj.uuid,
        status:
          obj.origin &&
          (obj.origin === 'BITSO'
            ? capitalizeFirst(obj.status_bitso)
            : capitalizeFirst(
                obj.origin === 'WALLET_CONVERSION' ||
                  obj.origin === 'WALLET_PAYMENT'
                  ? obj.deposit_status
                  : obj.status_plaid
              )),
        depositAmount: obj.deposit_amount,
        type: capitalizeFirst(obj.type),
        origin: capitalizeFirst(obj.origin),
        updated_at: obj.updated_at,
        amount: obj.amount,
        currency: obj.currency,
        user: obj.user,
      }));
    }
    return [];
  }, [balances]);

  const openPopupConvert = () => {
    if (!profile?.isFullRegister) {
      navigate('/onboarding');
      return;
    }
    showConvertModal();
  };

  return (
    <DataLoadingWrapper
      loadingData={loadingData || !data}
      customLoader={
        <Box className="animate-pulse w-full min-h-[80vh]">
          <Box className="h-10 bg-gray-300" />
        </Box>
      }
    >
      {data && (
        <Box className="min-h-[80vh]">
          <RenderContent data={data} showConvertModal={openPopupConvert} />
        </Box>
      )}
      {isConvertModalOpen && dataMixed && (
        <ConvertModal
          open={isConvertModalOpen}
          wallets={dataMixed.walletsDataMixed}
          onClose={hideConvertModal}
          shouldReload={() => {
            hideConvertModal();
            reloadWallet();
          }}
        />
      )}
    </DataLoadingWrapper>
  );
};

export default Balances;
